import { motion } from 'framer-motion';
import { cn } from "@/lib/utils";

function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("skeleton rounded-xl", className)}
      {...props}
    />
  );
}

// Hero section skeleton
function HeroSkeleton() {
  return (
    <div className="h-[200px] sm:h-[280px] md:h-[350px] lg:h-[450px] xl:h-[500px] w-full skeleton rounded-none" />
  );
}

// Category skeleton
function CategorySkeleton() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
          className="aspect-[4/3] skeleton rounded-2xl"
        />
      ))}
    </div>
  );
}

// Product card skeleton
function ProductCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
      <div className="aspect-square skeleton rounded-none" />
      <div className="p-4 space-y-3">
        <div className="h-4 skeleton rounded-lg" />
        <div className="h-3 skeleton rounded-lg w-2/3" />
        <div className="h-6 skeleton rounded-lg w-1/2" />
      </div>
    </div>
  );
}

// Product grid skeleton
function ProductGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <>
      {[...Array(count)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05 }}
        >
          <ProductCardSkeleton />
        </motion.div>
      ))}
    </>
  );
}

// Product detail skeleton
function ProductDetailSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 mobile-page">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Image Skeleton */}
          <div className="space-y-4">
            <div className="aspect-square skeleton rounded-2xl" />
            <div className="grid grid-cols-4 gap-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-square skeleton rounded-xl" />
              ))}
            </div>
          </div>

          {/* Details Skeleton */}
          <div className="space-y-6">
            <div className="h-8 skeleton rounded-lg w-3/4" />
            <div className="h-6 skeleton rounded-lg w-1/4" />
            <div className="h-10 skeleton rounded-lg w-1/3" />
            <div className="space-y-2">
              <div className="h-4 skeleton rounded-lg" />
              <div className="h-4 skeleton rounded-lg" />
              <div className="h-4 skeleton rounded-lg w-2/3" />
            </div>
            <div className="h-12 skeleton rounded-xl" />
            <div className="h-12 skeleton rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Cart skeleton
function CartSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 mobile-page">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="h-8 skeleton rounded-lg w-48 mb-8" />
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-4 shadow-soft">
                <div className="flex gap-4">
                  <div className="w-24 h-24 skeleton rounded-xl flex-shrink-0" />
                  <div className="flex-1 space-y-3">
                    <div className="h-5 skeleton rounded-lg w-3/4" />
                    <div className="h-4 skeleton rounded-lg w-1/2" />
                    <div className="h-6 skeleton rounded-lg w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-soft space-y-4 sticky top-24">
              <div className="h-6 skeleton rounded-lg w-1/2" />
              <div className="space-y-2">
                <div className="h-4 skeleton rounded-lg" />
                <div className="h-4 skeleton rounded-lg" />
                <div className="h-4 skeleton rounded-lg" />
              </div>
              <div className="h-12 skeleton rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Wishlist skeleton
function WishlistSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: i * 0.05 }}
        >
          <ProductCardSkeleton />
        </motion.div>
      ))}
    </div>
  );
}

// List skeleton
function ListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-4">
      {[...Array(count)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1 }}
          className="bg-white rounded-2xl p-6 shadow-soft"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 skeleton rounded-xl flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-5 skeleton rounded-lg w-3/4" />
              <div className="h-4 skeleton rounded-lg w-1/2" />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// Order tracking skeleton
function OrderTrackingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-soft p-8">
        <div className="space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-2xl shadow-soft p-8">
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex justify-between items-center p-3 border border-gray-200 rounded-xl">
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export {
  Skeleton,
  ProductCardSkeleton,
  ProductGridSkeleton,
  ProductDetailSkeleton,
  CartSkeleton,
  WishlistSkeleton,
  CategorySkeleton,
  HeroSkeleton,
  ListSkeleton,
  OrderTrackingSkeleton,
};
